import React, { useState } from "react";
import { Phone, Mail, MapPin, Loader2, CheckCircle2, Instagram } from "lucide-react";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { GoogleMap, HoursTable } from "../components/SharedSections";
import { CLINIC, FAQS } from "../lib/data";
import { createContact } from "../lib/api";

const initial = { name: "", email: "", phone: "", subject: "General Enquiry", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await createContact(form);
      setDone(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch {
      toast.error("Something went wrong. Please call us on " + CLINIC.phone);
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    { icon: Phone, label: "Call us", value: CLINIC.phone, href: `tel:${CLINIC.phoneIntl}`, external: false },
    { icon: Mail, label: "Email us", value: CLINIC.email, href: `mailto:${CLINIC.email}`, external: false },
    { icon: MapPin, label: "Visit us", value: CLINIC.address, href: "https://www.google.com/maps?q=1A+Sydenham+Street,+Sandringham+VIC+3191", external: true },
    { icon: Instagram, label: "Follow us", value: "@arena_movement_physio", href: CLINIC.socials[0].url, external: true },
  ];

  return (
    <>
      <Seo title="Contact" description="Contact Arena Movement Physiotherapy in Sandringham & Highett, Melbourne. Call 0414 606 084 or send us a message. Physio consultations Mondays." path="/contact" />
      <PageHeader
        eyebrow="Get in Touch"
        title="We'd love to hear from you"
        subtitle="Have a question or ready to book? Reach out and our friendly team will get back to you."
      />

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.06}>
              <a href={c.href} target={c.external ? "_blank" : undefined} rel="noreferrer" className="block bg-white rounded-3xl border border-border-light p-7 card-hover" data-testid={`contact-card-${c.label.split(" ")[0].toLowerCase()}`}>
                <div className="h-12 w-12 grid place-items-center rounded-2xl bg-navy text-white"><c.icon className="h-6 w-6" /></div>
                <div className="mt-4 text-xs font-bold uppercase tracking-[0.15em] text-navy/60">{c.label}</div>
                <div className="mt-1 font-medium text-text-dark break-words">{c.value}</div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10">
          {/* FORM */}
          <Reveal>
            <div className="bg-white rounded-3xl border border-border-light p-7 sm:p-10">
              <h2 className="font-display text-2xl font-semibold text-text-dark">Send a message</h2>
              {done ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-emerald-50"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
                  <h3 className="mt-5 font-display text-xl font-bold text-text-dark">Message sent!</h3>
                  <p className="mt-2 text-text-muted">Thanks for reaching out, we'll be in touch soon.</p>
                  <button onClick={() => { setForm(initial); setDone(false); }} className="mt-6 rounded-full border border-navy text-navy px-6 py-3 font-semibold hover:bg-navy hover:text-white transition-colors" data-testid="contact-reset">Send another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-5" data-testid="contact-form">
                  <input data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Full name *" className="cform" />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} placeholder="Email *" className="cform" />
                    <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder="Phone" className="cform" />
                  </div>
                  <input data-testid="contact-subject" value={form.subject} onChange={update("subject")} placeholder="Subject" className="cform" />
                  <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={5} placeholder="Your message *" className="cform resize-none" />
                  <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-navy px-7 py-4 font-semibold text-white hover:bg-navy-hover transition-all disabled:opacity-60">
                    {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          {/* MAP + HOURS */}
          <Reveal delay={0.1}>
            <div className="space-y-6">
              <GoogleMap className="h-[320px]" />
              <HoursTable />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28 bg-offwhite" data-testid="contact-faq">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-navy">FAQs</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-text-dark tracking-tight">Frequently asked questions</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-8">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border-light" data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-display text-lg font-medium text-text-dark hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-text-muted leading-relaxed text-base">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <style>{`
        .cform {
          width: 100%;
          border: 1px solid var(--border-light);
          border-radius: 0.9rem;
          padding: 0.85rem 1rem;
          font-size: 1rem;
          color: var(--text-dark);
          background: #fff;
          transition: border-color .2s, box-shadow .2s;
          outline: none;
        }
        .cform:focus { border-color: var(--navy); box-shadow: 0 0 0 3px rgba(11,37,69,0.08); }
      `}</style>
    </>
  );
}
